import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatEevnText } from '../../utils/formatEevnText';
import './EventModal.css';

const EventModal = ({ event, onClose }) => {
  const { t, i18n } = useTranslation();

  const getLocalized = (field, lang) => {
    if (!field || typeof field === 'string') return field;
    return field[lang] ?? field['en'] ?? '';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const locale = i18n.language === 'cs' ? 'cs-CZ' : 'en-US';
    return date.toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Store original styles
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const originalTransition = document.body.style.transition;

    // Disable body transitions temporarily to prevent blinking
    document.body.style.transition = 'none';

    // Calculate scrollbar width before hiding scroll
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // Apply styles synchronously to prevent blinking
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    // Re-enable transitions after a brief delay
    requestAnimationFrame(() => {
      document.body.style.transition = originalTransition || '';
    });

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      // Restore original styles
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
      document.body.style.transition = originalTransition;
    };
  }, [onClose]);

  if (!event) return null;

  return (
    <div className="event-modal" onClick={onClose}>
      <button className="event-modal-close" onClick={onClose} aria-label="Close">
        ✕
      </button>
      <div className="event-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Image */}
        {event.image && (
          <div className="event-modal-image">
            <img
              src={event.image}
              alt={event.venue}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}

        {/* Header: Venue & Location */}
        <div className="event-modal-header">
          <h2 className="event-modal-venue">{event.venue}</h2>
          <p className="event-modal-location">{event.location}</p>
        </div>

        {/* Date & Time */}
        <div className="event-modal-datetime">
          <div className="event-modal-datetime-item">
            <div className="event-modal-section-label">{t('events.date')}</div>
            <p className="event-modal-date">{formatDate(event.date)}</p>
          </div>
          {event.time && (
            <div className="event-modal-datetime-item">
              <div className="event-modal-section-label">{t('events.time')}</div>
              <p className="event-modal-time">{event.time}</p>
            </div>
          )}
        </div>

        {/* Address & Map Link */}
        {event.address && (
          <div className="event-modal-address">
            <div className="event-modal-section-label">{t('events.address')}</div>
            <p className="event-modal-address-text">
              {event.address}
              {event.link && (
                <a
                  href={event.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="event-modal-address-link"
                >
                  {t('events.viewOnMap')} →
                </a>
              )}
            </p>
          </div>
        )}

        {/* Description */}
        {(event.detailedDescription || event.description) && (
          <div className="event-modal-description">
            <div className="event-modal-section-label">{t('events.details')}</div>
            <p className="event-modal-description-text">
              {formatEevnText(getLocalized(event.detailedDescription || event.description, i18n.language))}
            </p>
          </div>
        )}

        {/* Other Bands */}
        {event.otherBands && event.otherBands.length > 0 && (
          <div className="event-modal-other-bands">
            <div className="event-modal-section-label">{t('events.otherBands')}</div>
            <div className="event-modal-bands-list">
              {event.otherBands.map((band, index) => (
                <div key={index} className="event-modal-band-item">
                  {band.link ? (
                    <a
                      href={band.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="event-modal-band-name-link"
                    >
                      {formatEevnText(band.name)}
                    </a>
                  ) : (
                    <p className="event-modal-band-name">{formatEevnText(band.name)}</p>
                  )}
                  {band.description && (
                    <p className="event-modal-band-description">{formatEevnText(getLocalized(band.description, i18n.language))}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tickets */}
        {event.ticketsInfo && (
          <div className="event-modal-tickets">
            <div className="event-modal-section-label">{t('events.tickets')}</div>
            <p className="event-modal-tickets-text">
              {event.ticketsInfo === 'cash' ? t('events.ticketsCash') : event.ticketsInfo}
            </p>
          </div>
        )}

        {/* Map */}
        {event.location && process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
          <div className="event-modal-map">
            <div className="event-modal-section-label">{t('events.map')}</div>
            <iframe
              title={`Map of ${event.venue}`}
              src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(`${event.venue}, ${event.location}`)}`}
              width="100%"
              height="250"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EventModal;
